import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCategoriesV2,
  getDollarGeneralCategories,
  getHomeDepotCategories,
  getTargetCategories,
} from "../services/CategoriesService";
import { patchUser, type User } from "../services/UserService";
import { toast } from "react-toastify";

type UserConfigUpdate = Partial<User> & {
  lastStoreChange?: number;
  lastHomeDepotStoreChange?: number;
  lastDollarGeneralStoreChange?: number;
  lastTargetStoreChange?: number;
  lastBestBuyStoreChange?: number;
  lastCostcoStoreChange?: number;
  lastLowesStoreChange?: number;
};

// Query keys
export const botFiltersKeys = {
  all: ["botFilters"] as const,
  categories: () => [...botFiltersKeys.all, "categories"] as const,
  homeDepotCategories: () =>
    [...botFiltersKeys.all, "homeDepotCategories"] as const,
  targetCategories: () => [...botFiltersKeys.all, "targetCategories"] as const,
  dollarGeneralCategories: () =>
    [...botFiltersKeys.all, "dollarGeneralCategories"] as const,
};

/**
 * Hook for fetching categories (root categories only)
 */
export const useCategories = () => {
  return useQuery({
    queryKey: botFiltersKeys.categories(),
    queryFn: getCategoriesV2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useDollarGeneralCategories = () => {
  return useQuery({
    queryKey: botFiltersKeys.dollarGeneralCategories(),
    queryFn: getDollarGeneralCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for fetching Home Depot categories (root categories only)
 */
export const useHomeDepotCategories = () => {
  return useQuery({
    queryKey: botFiltersKeys.homeDepotCategories(),
    queryFn: getHomeDepotCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useTargetCategories = () => {
  return useQuery({
    queryKey: botFiltersKeys.targetCategories(),
    queryFn: getTargetCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook for updating user configuration (categories, search terms, stores)
 */
export const useUpdateUserConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UserConfigUpdate) => {
      const updateData: UserConfigUpdate = { ...data };

      if (updateData.homeDepotStoreIds !== undefined) {
        updateData.homeDepotStoreIds = [...new Set(updateData.homeDepotStoreIds
          .map((storeId) => storeId.trim())
          .filter(Boolean)
          .map((storeId) => storeId.replace(/^0+/, "") || storeId))];
      }

      return patchUser(updateData);
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["user"] });

      const previousUser = queryClient.getQueryData<User>(["user"]);

      // Avoid optimistic store-ID updates; the server may reject exhausted daily credits.
      const {
        storeIds,
        homeDepotStoreIds,
        targetStoreIds,
        dollarGeneralStoreIds,
        bestbuyStoreIds,
        costcoStoreIds,
        lowesStoreIds,
        ...safeVariables
      } = variables;

      queryClient.setQueryData<User>(["user"], (old) => {
        if (!old) return old;
        return { ...old, ...safeVariables };
      });

      return { previousUser };
    },
    onError: (error: unknown, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousUser) {
        queryClient.setQueryData(["user"], context.previousUser);
      }

      // Handle store change restriction error specifically
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        (error as { status: number }).status === 425 &&
        (variables.storeIds !== undefined ||
          variables.homeDepotStoreIds !== undefined ||
          variables.targetStoreIds !== undefined ||
          variables.dollarGeneralStoreIds !== undefined ||
          variables.bestbuyStoreIds !== undefined ||
          variables.costcoStoreIds !== undefined ||
          variables.lowesStoreIds !== undefined)
      ) {
        // Error will be handled in the component
        throw error;
      } else {
        toast.error("Failed to save configuration");
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData<User>(["user"], (old) =>
        old ? { ...old, ...data } : data
      );
    },
    onSettled: () => {
      // Always refetch user data after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
};
